import type { CalendarEventDto } from '../../api/calendar';

/**
 * ⛔⛔ **반복 일정은 «끌 수 없다»** (2026-10-05 사용자 결정)
 *
 * 사용자 원문: 「반복일정은 이동이 안되도록하자.」 ⇒ (좁혀 물은 뒤) 「b 드래그를 못하도록하자.」
 *
 * ## ⛔ 왜 — 운영에서 터졌다
 *
 * 2026-10-04 에 수정·이동의 「전체 일정」이 되살아났고(#2158), 다음 날 사용자 실측 보고가 왔다:
 * 「반복(월요일 2건) 중 앞의 것을 10/6(화)로 옮기고 «모든 일정»을 골랐더니 뒤의 10/12 가 사라졌다」
 * ⇒ #2158 은 **되돌렸다**(그것이 「전체 일정」을 다시 뺀다). ★ 이 파일은 **그 «다음» 칸**이다 —
 *   「전체 일정」이 없어도 **끌기 자체**가 남아 있어, 「이 일정만」·「이후 일정 모두」로 갈 수 있다.
 *
 * ## ★ 잰 것 — 끌기가 남아 있으면 무엇이 위험한가
 *
 * 제품 코드(`calendar-recurrence.ts` 의 `expandOccurrences`·`truncateRruleUntil`·
 * `recurrenceRuleForFutureSplit`)를 **불러** 전개 결과를 쟀다. 10/05 회차를 10/06 으로:
 * ```
 *   「이후 일정 모두」 + FREQ=WEEKLY           새 master FREQ=WEEKLY          → 10/06 10/13 … ✅
 *   「이후 일정 모두」 + FREQ=WEEKLY;BYDAY=MO  새 master FREQ=WEEKLY;BYDAY=MO → 10/12 10/19 … ⛔
 *                                              ⇒ ★ 옮긴 «10/06 이 빈다»
 *   「이 일정만」                                exception upsert 만 — 규칙·master 를 안 건드린다 ✅
 * ```
 * ⇒ `recurrenceRuleForFutureSplit` 은 **COUNT 만** 고치고 `BYDAY` 는 그대로 물려준다
 *   (`calendar-recurrence.ts:245-269`) ⇒ 새 master 의 DTSTART 만 화요일로 가 **규칙과 어긋난다.**
 * ⇒ `BYDAY` 를 보내는 쪽은 **앱(iOS·AOS)뿐**이고 웹은 안 보낸다(보내는 곳 0건) — 그래서
 *   **웹에서 만든 시리즈는 안전하고 앱에서 만든 시리즈만 깨진다**. 화면이 그것을 가를 수 없다.
 * ⇒ ★ 그래서 「어느 범위가 안전한가」로 가르지 않고 **끌기를 통째로 막는다**(사용자 결정).
 *
 * ## ⚠ 막지 «않는» 것
 *
 * - **상세·수정 화면의 날짜 바꾸기는 그대로다** — 사용자가 ⓑ(드래그만)를 고르셨고 ⓒ(아예)가 아니다.
 * - **반복이 아닌 일정은 한 글자도 안 바뀐다** — 그것이 이 판의 대조군이다.
 * - 삭제·클릭·툴팁은 손대지 않는다.
 *
 * ## ⇒ 되살릴 조건 (그 날이 오면)
 *
 * 서버가 **DTSTART 를 밀 때 `BYDAY`(그리고 `BYMONTHDAY`·`BYSETPOS`)를 함께 다루게** 되고,
 * 「**회차 둘 이상에서 뒤 회차가 그대로 있나**」를 재는 시험이 서거든, **사용자 결정을 새로 받아**
 * 푼다. ⛔ 「서버가 시리즈를 민다」만으로는 근거가 안 된다 — `planSeriesShift` 는 이미 들어와
 * 있었고도 이 사고가 났다(`RecurrenceScopeDialog.tsx` 머리말에 그 전말이 있다).
 */
export function isDragBlocked(e: Pick<CalendarEventDto, 'recurrenceRule'>): boolean {
  return !!e.recurrenceRule;
}
